<template>
  <div class="contact-form max-w-[770px] mx-auto flex flex-col gap-8 lg:gap-12">
    <div class="contact-form__header flex flex-col gap-5 lg:gap-6 text-left lg:text-center">
      <h2 class="contact-form__title">{{ component.contactFormTitle }}</h2>
      <div class="contact-form__description text-regular-normal">
        {{ component.contactFormDescription }}
      </div>
    </div>
    <Form
      class="contact-form__form flex flex-col gap-6 md:flex-row md:flex-wrap"
      :validation-schema="schema"
      @submit="onSubmit"
      @invalidSubmit="onInvalidSubmit"
      @input="onFormTouch"
      v-slot="{ isSubmitting, meta }"
    >
      <input type="hidden" name="language" :value="locale" />
      <div class="form-control form-control__row text-small-normal">
        {{ t('components.contactForm.requiredText') }}
      </div>
      <div class="form-control form-control__row md:form-control__row--2 md:min-w-[40%]">
        <Field name="firstName" v-slot="{ field, errorMessage, meta }" validateOnInput>
          <SharedInput
            :label="t('components.contactForm.firstName')"
            :id="`${compoentId}-firstName`"
            name="firstName"
            type="text"
            required
            :error="errorMessage"
            :model-value="field.value"
            @update:model-value="field.onInput"
          />
        </Field>
      </div>
      <div class="form-control form-control__row md:form-control__row--2 md:min-w-[40%]">
        <Field name="lastName" v-slot="{ field, errorMessage, meta }" validateOnInput>
          <SharedInput
            :label="t('components.contactForm.lastName')"
            :id="`${compoentId}-lastName`"
            name="lastName"
            type="text"
            required
            :error="errorMessage"
            :model-value="field.value"
            @update:model-value="field.onInput"
          />
        </Field>
      </div>
      <div class="form-control form-control__row md:form-control__row--2 md:min-w-[40%]">
        <Field name="email" v-slot="{ field, errorMessage, meta }" validateOnInput>
          <SharedInput
            :label="t('components.contactForm.email')"
            :id="`${compoentId}-email`"
            name="email"
            type="email"
            autocomplete="email"
            required
            :error="meta.touched && errorMessage ? errorMessage : undefined"
            :model-value="field.value"
            @update:model-value="field.onInput"
          />
        </Field>
      </div>
      <div class="form-control form-control__row md:form-control__row--2 md:min-w-[40%]">
        <Field name="tel" v-slot="{ field, errorMessage, meta }" validateOnInput>
          <SharedInput
            :label="t('components.contactForm.tel')"
            :id="`${compoentId}-tel`"
            name="tel"
            type="tel"
            autocomplete="tel"
            required
            :error="meta.touched && errorMessage ? errorMessage : undefined"
            :model-value="field.value"
            @update:model-value="field.onInput"
          />
        </Field>
      </div>
      <div class="form-control form-control__row">
        <Field name="bestReachable" v-slot="{ field, errorMessage, meta }" validateOnInput>
          <SharedInput
            :label="t('components.contactForm.bestReachable')"
            :id="`${compoentId}-bestReachable`"
            name="bestReachable"
            type="text"
            :error="meta.touched && errorMessage ? errorMessage : undefined"
            :model-value="field.value"
            @update:model-value="field.onInput"
          />
        </Field>
      </div>
      <div class="form-control form-control__row">
        <Field name="message" v-slot="{ field, errorMessage, meta }" validateOnInput>
          <SharedTextarea
            :label="t('components.contactForm.message')"
            :id="`${compoentId}-message`"
            name="message"
            :placeholder="t('components.contactForm.messagePlaceholder')"
            required
            :error="meta.touched && errorMessage ? errorMessage : undefined"
            :model-value="field.value"
            @update:model-value="field.onInput"
            :rows="10"
          />
        </Field>
      </div>
      <div class="form-control form-control__row">
        <Field name="acceptTerms" v-slot="{ field, errorMessage, meta }" validateOnInput>
          <SharedCheckbox :id="`${compoentId}-acceptTerms`" name="acceptTerms" :model-value="!!field.value" required @update:modelValue="field.onInput" :error="errorMessage">
            <i18n-t keypath="components.newsletterSignupForm.acceptTerms" scope="global">
              <NuxtLink
                class="text-regular-normal !font-bold underline"
                :target="'_blank'"
                rel="noopener noreferrer"
                :to="`${privacyPolicyUrl}`"
                :title="siteOptions?.data?.dataProtectionPolicy?.title"
              >
                {{ t('components.contactForm.acceptTermsLink') }}
              </NuxtLink>
            </i18n-t>
          </SharedCheckbox>
        </Field>
      </div>
      <Transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 transform translate-y-2"
        enter-to-class="opacity-100 transform translate-y-0"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 transform translate-y-0"
        leave-to-class="opacity-0 transform translate-y-2"
      >
        <div v-if="formTouched && !validTurnstileToken" class="form-control form-control__row">
          <NuxtTurnstile v-model="turnstileToken" />
        </div>
      </Transition>
      <SharedButton
        class="w-max h-min mt-4"
        type="submit"
        :label="t('components.contactForm.submit')"
        :disabled="(hasSubmitted && !meta.valid && formTouched && !turnstileToken) || isSubmitting"
        :loading="isSubmitting"
      />
    </Form>
  </div>
</template>

<script setup lang="ts">
  import type { GraphQLContactForm, GraphQLCarDetail } from '~/types/graphql';
  import { useForm, Field, Form } from 'vee-validate';
  import { z } from 'zod';
  import { toTypedSchema } from '@vee-validate/zod';
  import type { StrapiResponse, SiteOption } from '~/types/strapi';
  import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';

  interface EventData {
    event: 'contact_form_started' | 'contact_form_submitted' | 'contact_form_error';
    form_type: 'contact_form';
    email?: string;
    phone?: string;
    page_location: string;
    vehicle_id?: string;
    make?: string;
    model?: string;
    manufactured_at?: string;
    price?: number;
    fuel_type?: string;
    user_data?: {
      sha256_email_address?: string;
      sha256_phone_number?: string;
    }
  }

  const siteOptions = inject('siteOptions') as Ref<StrapiResponse<SiteOption>> | undefined;
  const privacyPolicyUrl = computed(() => useLocalizedPath(siteOptions?.value?.data?.dataProtectionPolicy?.slug || ''));

  const compoentId = useId();

  defineProps<{
    component: GraphQLContactForm;
    index: number;
  }>();

  const carDetail = inject<GraphQLCarDetail | undefined>('carDetail', undefined);

  const { locale, t } = useI18n();

  const schema = toTypedSchema(
    z.object({
      email: z
        .string({ required_error: t('validation.required') })
        .email(t('validation.email'))
        .min(1, t('validation.required')),
      firstName: z.string({ required_error: t('validation.required') }).min(1, t('validation.required')),
      lastName: z.string({ required_error: t('validation.required') }).min(1, t('validation.required')),
      tel: z
        .string({ required_error: t('validation.required') })
        .min(1, t('validation.required'))
        .refine(val => isValidPhoneNumber(val, { defaultCountry: locale.value.toUpperCase() as any }), t('validation.phoneNumber')),
      bestReachable: z.string().optional(),
      message: z.string({ required_error: t('validation.required') }).min(1, t('validation.required')),
      acceptTerms: z.boolean({ required_error: t('validation.required') }).refine(val => val, t('validation.required')),
    })
  );

  const hasSubmitted = ref(false);
  const formTouched = ref(false);

  const { validTurnstileToken, turnstileToken } = useValidTurnstileTokenCookie();
  const eventBus = useEventBus();

  const { push } = useDataLayer();

  const onFormTouch = () => {
    if (formTouched.value) {
      return;
    }

    const eventData: EventData = {
      event: 'contact_form_started',
      form_type: 'contact_form',
      page_location: window.location.href,
    };

    if (carDetail) {
      eventData.vehicle_id = carDetail.internal_vehicle_no;
      eventData.make = carDetail.make?.name;
      eventData.model = carDetail.model;
      eventData.manufactured_at = carDetail.year_of_manufacture?.toString() || undefined;
      eventData.price = carDetail.asking_price;
      eventData.fuel_type = carDetail.fuel_type;
    }

    push(eventData);
    formTouched.value = true;
  };

  const onInvalidSubmit = () => {
    formTouched.value = true;
  };

  const onSubmit = async (values: any, { resetForm, setTouched }: { resetForm: () => void; setTouched: (touched: boolean) => void }) => {
    if (hasSubmitted.value || !validTurnstileToken.value) {
      return;
    }
    hasSubmitted.value = true;
    setTouched(true);

    const formValues = { ...values, language: locale.value };
    if (carDetail?.internal_vehicle_no) {
      formValues.vehicleNo = carDetail?.internal_vehicle_no;
    }

    const eventData: EventData = {
      event: 'contact_form_submitted',
      form_type: 'contact_form',
      page_location: window.location.href,
      user_data: {
        sha256_email_address: await useShaHash(values.email),
        sha256_phone_number: await useShaHash(values.tel),
      },
    };

    if (carDetail) {
      eventData.vehicle_id = carDetail.internal_vehicle_no;
      eventData.make = carDetail.make?.name;
      eventData.model = carDetail.model;
      eventData.manufactured_at = carDetail.year_of_manufacture?.toString() || undefined;
      eventData.price = carDetail.asking_price;
      eventData.fuel_type = carDetail.fuel_type;
    }

    push(eventData);

    try {
      const response = await fetch('/api/contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formValues),
      });

      if (!response.ok) {
        hasSubmitted.value = false;
        throw new Error('Failed to submit form');
      }

      eventBus.emit('add-global-toast', {
        show: true,
        title: t('components.contactForm.success'),
        message: t('components.contactForm.successMessage'),
        duration: 3000,
        type: 'foreground',
        animation: 'fadeIn',
        error: false,
      });

      resetForm();
      hasSubmitted.value = false;
    } catch (error) {
      hasSubmitted.value = false;
      eventBus.emit('add-global-toast', {
        show: true,
        title: t('components.contactForm.error'),
        message: t('components.contactForm.errorMessage'),
        duration: 3000,
        type: 'foreground',
        animation: 'fadeIn',
        error: true,
      });
    }
  };
</script>
